import { useQuery } from "@tanstack/react-query";
import { Zap } from "lucide-react";
import { openBookingLightbox } from "@/components/booking/booking-lightbox";

/**
 * One home's configured cap and exact bookable stays, quoted by the server.
 * A configured offer with no eligible stays has no booking link.
 */

export type LastMinuteWindow = {
  start: string;
  end: string;
  nights: number;
  minNights: number;
  nightlyRate: number;
  regularNightlyRate: number;
};

export type LastMinuteOfferData = {
  active: boolean;
  propertyId: string;
  propertySlug: string;
  propertyName: string;
  title?: string;
  nightlyRate: number;
  cutoff: string;
  windows: LastMinuteWindow[];
};

export function useLastMinuteOffer(slug?: string | null) {
  return useQuery<LastMinuteOfferData>({
    queryKey: [`/api/properties/${slug}/last-minute`],
    enabled: Boolean(slug),
    staleTime: 0,
    refetchInterval: 60 * 1000,
  });
}

const badgeStyle = "relative z-10 inline-flex max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 rounded-full bg-sun/15 px-3 py-1 text-center font-sans text-[12px] text-ink";

const money = (n: number) => `$${Number(n).toLocaleString("en-US", {
  minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2,
  maximumFractionDigits: 2,
})}`;
const day = (ymd: string) =>
  new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

function bookingHref(slug: string, window: LastMinuteWindow) {
  const params = new URLSearchParams({ start: window.start, end: window.end });
  return `/properties/${encodeURIComponent(slug)}/book?${params.toString()}`;
}

/**
 * `variant="panel"` for listing pages and the lightbox; `variant="badge"` is a
 * one-line version for home cards. `onPick` replaces the default (open the
 * lightbox) when the caller is already inside it.
 */
export function LastMinuteOffer({
  slug,
  variant = "panel",
  className = "",
  onPick,
  embedded = false,
}: {
  slug?: string | null;
  embedded?: boolean;
  variant?: "panel" | "badge";
  className?: string;
  onPick?: (start: string, end: string) => void;
}) {
  const { data, isLoading, isError } = useLastMinuteOffer(slug);
  if (!slug) return null;
  if (isLoading || isError) return <p className={`text-xs text-ink-soft ${className}`} role="status">{isLoading ? 'Checking last-minute stays…' : 'Last-minute availability could not be checked. Please try again.'}</p>;
  if (!slug || !data?.propertySlug || data.propertySlug !== slug) return null;
  const propertySlug = data.propertySlug;
  const windows = Array.isArray(data.windows) ? data.windows : [];
  const pick = (w: LastMinuteWindow) => onPick?.(w.start, w.end);
  const name = data.propertyName || "This property";

  if (!data.active || windows.length === 0) {
    const message = "No last-minute stays available right now";
    if (variant === "badge") return <span className={`${badgeStyle} ${className}`} data-testid={`last-minute-empty-${slug}`}><Zap className="h-3.5 w-3.5 shrink-0 text-sun" aria-hidden="true" /><span className="font-semibold">{message}</span></span>;
    const empty = <div className="pb-3" data-testid={`last-minute-empty-${slug}`}><p className="flex flex-wrap items-center gap-2 font-sans text-[11px] uppercase tracking-[0.2em] text-ink"><Zap className="h-3.5 w-3.5 shrink-0 text-sun" aria-hidden="true" />Last-minute rate · {name}</p><p className="mt-1 font-sans text-[12px] leading-relaxed text-ink/70">{message}. Choose dates for standard or seasonal pricing.</p></div>;
    return embedded ? empty : <details data-card-ignore className={`min-w-0 rounded border border-sun/40 bg-sun/10 px-3 ${className}`}><summary className="min-h-11 cursor-pointer py-3 font-sans text-sm font-semibold">Last-minute stays · View dates &amp; rates</summary>{empty}</details>;
  }

  const first = windows[0];
  const pickWindow = (w: LastMinuteWindow) => {
    if (onPick) pick(w);
    else if (propertySlug) openBookingLightbox({ slug: propertySlug, start: w.start, end: w.end });
  };
  const windowRate = (w: LastMinuteWindow) => {
    const validRegularRate = Number.isFinite(w.regularNightlyRate) && w.regularNightlyRate > w.nightlyRate;
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
        {validRegularRate && <s className="text-driftwood decoration-1">{money(w.regularNightlyRate)}</s>}
        <span className="font-semibold">{money(w.nightlyRate)}</span>
        <span className="text-ink/70">/ night</span>
      </span>
    );
  };

  if (variant === "badge") {
    const w = first;
    const contents = (
      <>
        <Zap className="h-3.5 w-3.5 shrink-0 text-sun" aria-hidden="true" />
        <span className="font-semibold">View last-minute stay</span>
        <span className="text-ink/80">{day(w.start)}–{day(w.end)} · {w.nights} nights</span>
      </>
    );
    const classes = `${badgeStyle} hover:bg-sun/25 ${className}`;
    const badge = onPick ? (
      <button type="button" onClick={(e) => { e.stopPropagation(); pickWindow(w); }} className={classes} data-testid={`last-minute-badge-${propertySlug || slug}`}>
        {contents}
      </button>
    ) : propertySlug ? (
      <a href={bookingHref(propertySlug, w)} onClick={(e) => e.stopPropagation()} className={classes} data-testid={`last-minute-badge-${propertySlug}`}>
        {contents}
      </a>
    ) : (
      <span className={classes} data-testid={`last-minute-badge-${slug}`}>{contents}</span>
    );
    return badge;
  }

  const panel = (      <div className="pb-3">
      <p className="flex flex-wrap items-center gap-2 font-sans text-[11px] uppercase tracking-[0.2em] text-ink">
        <Zap className="h-3.5 w-3.5 shrink-0 text-sun" aria-hidden="true" /> Last-minute rate · {name}
      </p>
      <p className="mt-1 font-sans text-[12px] leading-relaxed text-ink/70">
        Nightly rates in USD, before taxes and fees. Choose an eligible stay to see your complete quote.
      </p>
      <div className="mt-2 flex min-w-0 flex-col gap-2">
        {windows.map((w) => {
          const label = (
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium">{day(w.start)}–{day(w.end)}</span>
              <span>{w.nights} night{w.nights === 1 ? "" : "s"} · Select dates</span>
              {windowRate(w)}
            </span>
          );
          const classes = "w-full min-w-0 rounded-xl border border-ink/20 bg-shell px-3 py-2 text-left font-sans text-[12px] leading-relaxed text-ink hover:border-ink hover:bg-white";
          return onPick ? (
            <button key={`${w.start}-${w.end}`} type="button" onClick={() => pickWindow(w)} className={classes} data-testid={`last-minute-window-${w.start}`}>
              {label}
            </button>
          ) : propertySlug ? (
            <a key={`${w.start}-${w.end}`} href={bookingHref(propertySlug, w)} className={`${classes} block`} data-testid={`last-minute-window-${w.start}`}>
              {label}
            </a>
          ) : (
            <div key={`${w.start}-${w.end}`} className={classes} data-testid={`last-minute-window-${w.start}`}>
              {label}
            </div>
          );
        })}
      </div>
      </div>);
  if (embedded) return panel;

  return (
    <details data-card-ignore className={`min-w-0 rounded border border-sun/40 bg-sun/10 px-3 ${className}`} data-testid={`last-minute-offer-${propertySlug || slug}`}>
      <summary className="min-h-11 cursor-pointer py-3 font-sans text-sm font-semibold">Last-minute stays · View dates &amp; rates</summary>
      {panel}
    </details>
  );
}
